/**
 * Magic Link recipient (token tier) domain + adapter contract.
 *
 * Per BDSE_MAGIC_LINK_RECIPIENT_SPEC_v1.1: the recipient is ALWAYS an internal
 * Ankura consultant, assigned and known. This is the assigned-consultant
 * operating surface, not a guest view; actions are enabled and each is a ledger
 * event attributed to actor_type=token_recipient.
 *
 * The adapter seam mirrors the signals SignalActionsAdapter pattern: a mock
 * implementation drives the UI now, a dormant live implementation throws until
 * Codex's Gate 3 Decision 8 token functions land. No client-side Supabase
 * mutations happen on either path (scanner rule unchanged).
 */

import type { SignalRelationshipResult } from "@/components/RelationshipResults";

/** The five pursuit stages (spec §3). Current stage is derived from ledger. */
export const STAGE_ORDER = [
  "assigned",
  "outreach_made",
  "meeting",
  "proposal",
  "won",
] as const;

export type StageId = (typeof STAGE_ORDER)[number];

/** Terminal outcomes (spec §5). Both write a lifecycle transition. */
export type Outcome = "won" | "passed";
export type PassReasonCode = "not_a_fit" | "at_capacity" | "pto" | "other";
export interface PassReason {
  code: PassReasonCode;
  note?: string;
}

export type ContactStrength = "weak" | "moderate" | "strong";

export interface MatchedContact {
  id: string;
  name: string;
  title: string;
  strength: ContactStrength;
  /** true once saved to the recipient's BDSE contacts (Find Contacts results). */
  saved?: boolean;
}

/** A sibling signal at the same account (account pattern, spec §6). */
export interface RelatedSignal {
  id: string;
  company: string;
  headline: string;
  score: number;
}

export interface AccountPattern {
  company: string;
  activeCount: number;
  related: RelatedSignal[];
  /** Optional prior Ankura engagement provenance line. */
  priorEngagement: string | null;
}

/**
 * Why This Signal For You (R2, spec §3). Renders the assign context note plus a
 * capability-match line when available. The whole card is hidden if BOTH are
 * absent.
 */
export interface WhyForYou {
  contextNote: string | null;
  capabilityMatch: string | null;
}

export interface RecommendedAsset {
  title: string;
  kind: string;
  reason: string;
}

export interface EvidenceInfo {
  sourceLabel: string;
  sourceUrl: string | null;
  retrievedAt: string;
  status: "verified" | "unverified" | "flagged";
  deadlineAt: string | null;
}

export interface ActivationContext {
  relationship: string;
  detail: string;
  monetization: string;
}

export type HistoryActorType = "human" | "system" | "token_recipient";

export interface RecipientHistoryEntry {
  id: string;
  actorType: HistoryActorType;
  actorName: string;
  verb: string;
  detail: string | null;
  at: string;
}

export interface RecipientSignal {
  id: string;
  company: string;
  headline: string;
  score: number;
  tier: number;
  verification: EvidenceInfo["status"];
  updatedAt: string;
  firstSeenAt: string;
  summary: string;
  whyItMatters: string;
  recommendedAction: string;
  talkTrack: string;
}

/**
 * The full recipient brief returned by the token function (get_magic_link_signal_brief,
 * field policy per spec §5). A null brief from getBrief() means an expired,
 * revoked, or invalid token: the page renders the honest fallback, never signal
 * content.
 */
export interface RecipientBrief {
  token: string;
  recipientName: string;
  assignerName: string;
  observedAt: string;
  /**
   * When this signal was assigned to the recipient (assign-event occurred_at,
   * falling back to the signal-level assigned_at). Null when the brief carries no
   * assignment timestamp; the top bar then falls back to the observed date.
   */
  assignedAt: string | null;
  expiresAt: string;
  expiresInDays: number;
  snoozeUsed: number;
  snoozeMax: number;
  reachedStage: StageId;
  outcome: Outcome | null;
  signal: RecipientSignal;
  whyForYou: WhyForYou | null;
  accountPattern: AccountPattern | null;
  recommendedAsset: RecommendedAsset | null;
  matchedContacts: MatchedContact[];
  /** Persisted found contacts hydrated on brief open without consuming search cap. */
  foundContacts: MatchedContact[];
  evidence: EvidenceInfo;
  activation: ActivationContext;
  ownershipLabel: string;
  followUpDueAt: string | null;
  history: RecipientHistoryEntry[];
}

export interface SnoozeResult {
  expiresAt: string;
  expiresInDays: number;
  snoozeUsed: number;
}

/**
 * Token-tier adapter (spec §5 action set). Reads and mutations both route
 * through Edge Functions in production; the recipient never holds a session or
 * writes to Supabase directly.
 */
export interface RecipientTokenAdapter {
  /** Resolve + render the brief. null = expired/revoked/invalid token. */
  getBrief(token: string): Promise<RecipientBrief | null>;
  /**
   * Resolve just the recipient/assigner identity for a dead token so the expired
   * fallback can name the assigner (spec §2: "if identity resolvable"). Returns
   * null when the token is unresolvable/invalid. Never returns signal content.
   */
  resolveIdentity(token: string): Promise<{ assignerName: string } | null>;
  recordStageEvent(token: string, stage: StageId): Promise<void>;
  recordOutcome(token: string, outcome: Outcome, passReason?: PassReason): Promise<void>;
  /**
   * Walk back the pursuit stage. NOT available in this tier (R-P4-5): stage
   * regression is operator-only, and the token bearer path rejects every
   * stage_regressed with a 403 ("Magic Link recipients cannot regress pursuit
   * stage.") before any ledger write. Both adapters therefore reject with
   * RegressionNotAllowedError without reaching the network, and the recipient
   * surface exposes no walk-back affordance. The member is retained on the
   * contract so the refusal is modelled honestly rather than silently absent.
   */
  regressStage(token: string, toStage: StageId, reason?: string): Promise<void>;
  /**
   * Reopen a closed (won/passed) pursuit via record_magic_link_recipient_action
   * (action=outcome_reopened). On 201 the server projection returns the pursuit
   * to active and clears closed_outcome, so a brief refetch reads it as active.
   * Rejects with ReopenNotAllowedError when the link was minted without
   * outcome_reopened in allowed_actions (403), or ReopenConflictError when the
   * pursuit is no longer closed (409).
   */
  reopenPursuit(token: string): Promise<void>;
  snooze(token: string): Promise<SnoozeResult>;
  /**
   * Run the firm relationship check. Records the ledger event AND returns the
   * grouped-by-owner relationships payload (record_magic_link_recipient_action
   * now returns { event, result }); RecipientMain renders it below the run
   * buttons, identical to the internal modal.
   */
  runRelationshipCheck(token: string): Promise<SignalRelationshipResult>;
  runTariffScan(token: string): Promise<void>;
  sendAsset(token: string, assetTitle: string): Promise<void>;
  /** Seamless three-step proxy. Rejects with a CapReachedError past the daily cap. */
  findContacts(token: string): Promise<MatchedContact[]>;
  setFollowUp(token: string, dueAt: string): Promise<void>;
}

/** Thrown by findContacts when the per-token daily cap is hit (spec §4). */
export class CapReachedError extends Error {
  constructor(message = "Contact search limit reached for today.") {
    super(message);
    this.name = "CapReachedError";
  }
}

/** Thrown by snooze at the per-link snooze cap (spec §5, snooze_max_per_link). */
export class SnoozeCapError extends Error {
  constructor(message = "Snooze limit reached.") {
    super(message);
    this.name = "SnoozeCapError";
  }
}

/**
 * Thrown by reopenPursuit when the link's allowed_actions omit outcome_reopened
 * (server 403). Links minted before the recipient-reopen feature carry no such
 * grant; the UI renders an honest "ask for a fresh link" message rather than a
 * generic error.
 */
export class ReopenNotAllowedError extends Error {
  constructor(message = "This link doesn't allow reopening.") {
    super(message);
    this.name = "ReopenNotAllowedError";
  }
}

/** Thrown by reopenPursuit when the pursuit is not in a closed state (server 409). */
export class ReopenConflictError extends Error {
  constructor(message = "Only a closed pursuit can be reopened.") {
    super(message);
    this.name = "ReopenConflictError";
  }
}

/**
 * Thrown by regressStage on both adapters (R-P4-5). Stage regression is
 * operator-only; the token bearer path answers every stage_regressed with a
 * 403 and writes nothing. Neither adapter issues the request — the refusal is
 * modelled client-side so mock and live agree with the server.
 */
export class RegressionNotAllowedError extends Error {
  constructor(message = "Magic Link recipients cannot regress pursuit stage.") {
    super(message);
    this.name = "RegressionNotAllowedError";
  }
}

/** Thrown by the live adapter until Codex Decision 8 functions land. */
export class DormantLiveError extends Error {
  constructor(fn: string) {
    super(
      `Live token adapter is dormant: ${fn} lands with Gate 3 Decision 8. ` +
        `Phase D recipient UI runs on the mock adapter.`,
    );
    this.name = "DormantLiveError";
  }
}
